import {
	Button,
	FormControlLabel,
	Switch,
	TextField,
	Tooltip,
} from "@material-ui/core";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import AssignmentIcon from "@material-ui/icons/Assignment";
import EditIcon from "@material-ui/icons/Edit";
import ToggleOffIcon from "@material-ui/icons/ToggleOff";
import ToggleOnIcon from "@material-ui/icons/ToggleOn";
import VisibilityIcon from "@material-ui/icons/Visibility";
import Autocomplete from "@material-ui/lab/Autocomplete/Autocomplete";
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import {
	getOperarios,
	generateOperarioMobileAccessToken,
	postOperario,
	updateOperario,
	updateOperarioStatus,
} from "../../../../api/operario";
import { getOperarioCategories } from "../../../../api/operarioCategory";
import { getSuppliers } from "../../../../api/supplier";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import EditOperarioDialog from "../../../components/dialogs/EditOperarioDialog";
import FiltersCard from "../../../components/filters/Filter";
import Table, { buttonsStyle } from "../../../components/tables/table";

const initialFilters = {
	category: null,
	showOnlyActive: false,
};

function getEmptyOperario() {
	return {
		id: null,
		nombre: "",
		apellidos: "",
		operario_categoria_id: null,
		email: "",
		telefono: "",
		coste_hora: 0,
		es_externo: false,
		proveedor_id: null,
		activo: true,
	};
}

function getData(operarios) {
	return operarios.map((operario) => ({
		nombreCompleto: `${operario?.nombre ?? ""} ${operario?.apellidos ?? ""}`.trim(),
		categoria: operario?.categoria?.descripcion || "",
		email: operario?.email || "",
		telefono: operario?.telefono || "",
		coste_hora: Number(operario?.coste_hora || 0).toFixed(2),
		tipo: operario?.es_externo ? "Externo" : "Interno",
		activo: operario?.activo ? "Activo" : "Inactivo",
		id: operario?.id,
	}));
}

export default function OperariosPage() {
	const [data, setData] = useState([]);
	const [filteredData, setFilteredData] = useState([]);
	const [categories, setCategories] = useState([]);
	const [suppliers, setSuppliers] = useState([]);
	const [selectedOperario, setSelectedOperario] = useState(null);
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const [openEditOperarioDialog, setOpenEditOperarioDialog] = useState(false);
	const [refresh, setRefresh] = useState(false);
	const [collapsed, setCollapsed] = useState(true);
	const [filterOptions, setFilterOptions] = useState(initialFilters);
	const [appliedCategoryId, setAppliedCategoryId] = useState(null);

	const loggedUser = useSelector(
		(store) => store.authentication?.user,
		shallowEqual
	);
	const accessToken = loggedUser?.accessToken;

	useEffect(() => {
		getOperarioCategories(accessToken)
			.then((res) => {
				if (res.status === 200) {
					setCategories(res.data);
				}
			})
			.catch((error) => {
				alertError({
					error,
					customMessage: "No se han podido obtener las categorías de operario.",
				});
			});

		getOperarios(accessToken)
			.then((res) => {
				if (res.status === 200) {
					setData(res.data);
					setRefresh(false);
				}
			})
			.catch((error) => {
				alertError({
					error,
					customMessage: "No se han podido obtener los operarios.",
				});
			});

		getSuppliers(accessToken)
			.then((res) => {
				if (res.status === 200) {
					setSuppliers(res.data);
				}
			})
			.catch((error) => {
				alertError({
					error,
					customMessage: "No se han podido obtener los proveedores.",
				});
			});
	}, [refresh, accessToken]);

	useEffect(() => {
		setFilteredData(
			data.filter((operario) => {
				let filter = true;

				if (appliedCategoryId) {
					filter =
						filter &&
						operario?.operario_categoria_id === appliedCategoryId;
				}

				if (filterOptions.showOnlyActive) {
					filter = filter && operario?.activo;
				}

				return filter;
			})
		);
	}, [data, appliedCategoryId, filterOptions.showOnlyActive]);

	const handleSearch = () => {
		setAppliedCategoryId(filterOptions.category?.id || null);
	};

	const handleClearFilters = () => {
		setFilterOptions((currentFilters) => ({
			...currentFilters,
			category: null,
		}));
		setAppliedCategoryId(null);
	};

	const handlePendingAction = (message) => {
		alertSuccess({
			customMessage: message,
		});
	};

	const openOperarioEditor = (operario) => {
		setSelectedOperario(operario);
		setOpenEditOperarioDialog(true);
	};

	function buttonFormatter(cell) {
		const operario = data.find((item) => item.id === cell);

		return (
			<>
				<Tooltip title="Editar">
					<Button
						style={buttonsStyle}
						size="small"
						onClick={() => openOperarioEditor(operario)}
					>
						<EditIcon />
					</Button>
				</Tooltip>
				<Tooltip title={operario?.activo ? "Desactivar" : "Activar"}>
					<Button
						style={buttonsStyle}
						size="small"
						onClick={() => {
							setSelectedOperario(operario);
							setOpenConfirmDialog(true);
						}}
					>
						{operario?.activo ? (
							<ToggleOffIcon />
						) : (
							<ToggleOnIcon style={{ color: "red" }} />
						)}
					</Button>
				</Tooltip>
				<Tooltip title="Ver obras">
					<Button
						style={buttonsStyle}
						size="small"
						onClick={() =>
							handlePendingAction(
								`La vista de obras de ${operario?.nombre} estará disponible en una fase posterior.`
							)
						}
					>
						<VisibilityIcon />
						<AssignmentIcon style={{ fontSize: 14, marginLeft: -4 }} />
					</Button>
				</Tooltip>
				<Tooltip title="Ver horas">
					<Button
						style={buttonsStyle}
						size="small"
						onClick={() =>
							handlePendingAction(
								`La vista de horas de ${operario?.nombre} estará disponible en una fase posterior.`
							)
						}
					>
						<AccessTimeIcon />
					</Button>
				</Tooltip>
			</>
		);
	}

	const columns = [
		{ dataField: "nombreCompleto", text: "Nombre + apellidos", sort: true },
		{
			dataField: "categoria",
			text: "Categoría",
			sort: true,
			headerAlign: "center",
			align: "center",
		},
		{
			dataField: "email",
			text: "Email",
			sort: true,
			headerAlign: "center",
			align: "center",
		},
		{
			dataField: "telefono",
			text: "Teléfono",
			sort: true,
			headerAlign: "center",
			align: "center",
		},
		{
			dataField: "coste_hora",
			text: "Coste/hora",
			sort: true,
			headerAlign: "center",
			align: "center",
		},
		{
			dataField: "tipo",
			text: "Tipo",
			sort: true,
			headerAlign: "center",
			align: "center",
		},
		{
			dataField: "activo",
			text: "Estado",
			sort: true,
			headerAlign: "center",
			align: "center",
		},
		{ dataField: "id", text: "", formatter: buttonFormatter },
	];

	const renderFiltersContent = () => (
		<Autocomplete
			className="w-sm-50 w-100"
			id="autocomplete-operario-category-filter"
			options={categories}
			getOptionLabel={(option) => option?.descripcion || ""}
			value={filterOptions.category}
			onChange={(event, selected) => {
				setFilterOptions({
					...filterOptions,
					category: selected,
				});
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					label="Categoría"
					margin="normal"
					variant="outlined"
					InputLabelProps={{ shrink: true }}
				/>
			)}
		/>
	);

	const handleSaveOperario = (operario) => {
		const request = operario?.id
			? updateOperario(operario.id, operario, accessToken)
			: postOperario(operario, accessToken);

		request
			.then((res) => {
				if (res.status === 201 || res.status === 204) {
					alertSuccess({
						title: operario?.id ? "¡Guardado!" : "¡Creado!",
						customMessage: operario?.id
							? "Operario actualizado con éxito."
							: "Operario creado con éxito.",
					});
					setOpenEditOperarioDialog(false);
					setSelectedOperario(null);
					setRefresh(true);
				}
			})
			.catch((error) => {
				alertError({
					error,
					customMessage: operario?.id
						? "No se ha podido actualizar el operario."
						: "No se ha podido crear el operario.",
				});
			});
	};

	return (
		<>
			<Card>
				<CardHeader headerClassName="px-0">
					<CardHeader
						title="Operarios"
						sticky={false}
						headerClassName="d-flex justify-content-between w-100 py-3"
					>
						<CardHeaderToolbar>
							<button
								type="button"
								className="btn btn-primary"
								onClick={() => {
					openOperarioEditor(getEmptyOperario());
								}}
							>
								Nuevo operario
							</button>
						</CardHeaderToolbar>
					</CardHeader>
					<div className="w-100">
						<FiltersCard
							collapsable={false}
							filtersContent={renderFiltersContent}
							collapsed={collapsed}
							setCollapsed={setCollapsed}
							handleClearFilters={handleClearFilters}
							handleSearch={handleSearch}
						/>
					</div>
				</CardHeader>
				<CardBody>
					<div className="mb-4">
						<FormControlLabel
							control={
								<Switch
									checked={filterOptions.showOnlyActive}
									onChange={(event) =>
										setFilterOptions({
											...filterOptions,
											showOnlyActive: event.target.checked,
										})
									}
									color="primary"
								/>
							}
							label={
								filterOptions.showOnlyActive
									? "Mostrar solo activos"
									: "Mostrar todos"
							}
						/>
					</div>
					<Table
						data={getData(filteredData)}
						columns={columns}
						rowEvents={{
							onClick: (event, row) => {
								if (event.target.closest("button, a, input, label")) return;
								const operario = data.find((item) => item.id === row.id);
								if (operario) openOperarioEditor(operario);
							},
						}}
						rowStyle={{ cursor: "pointer" }}
					/>

					<ConfirmDialog
						title={`¿Estás seguro de que quieres ${
							selectedOperario?.activo ? "desactivar" : "activar"
						} este operario?`}
						open={openConfirmDialog}
						setOpen={setOpenConfirmDialog}
						onConfirm={() => {
							updateOperarioStatus(
								selectedOperario?.id,
								!selectedOperario?.activo,
								accessToken
							)
								.then((res) => {
									if (res.status === 204) {
										alertSuccess({
											title: selectedOperario?.activo
												? "¡Desactivado!"
												: "¡Activado!",
											customMessage: `Operario ${
												selectedOperario?.activo
													? "desactivado"
													: "activado"
											} con éxito.`,
										});
										setRefresh(true);
									}
								})
								.catch((error) => {
									alertError({
										error,
										customMessage: `No se ha podido ${
											selectedOperario?.activo
												? "desactivar"
												: "activar"
										} el operario.`,
									});
								});
						}}
					/>

					<EditOperarioDialog
						title={selectedOperario?.id ? "Editar operario" : "Nuevo operario"}
						open={openEditOperarioDialog}
						setOpen={setOpenEditOperarioDialog}
						data={selectedOperario}
						categories={categories}
						suppliers={suppliers}
						onSave={handleSaveOperario}
						onGenerateMobileAccess={async (operarioId) => {
							const response = await generateOperarioMobileAccessToken(operarioId, accessToken);
							return response.data.url_acceso;
						}}
					/>
				</CardBody>
			</Card>
		</>
	);
}
